import { message, notification } from '@/app/AntdGlobal';
import { getIntl } from '@/locales/intl';
import type { RequestOptions } from '@/services/request';
import { resetSession } from '@/services/session';
import { toExternalAppPath, toRouterPath } from '@/utils/appUrl';
import { browserStorage } from '@/utils/browserStorage';

export enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

export type ResponseStructure = {
  success: boolean;
  data?: unknown;
  errorCode?: number | string;
  errorMessage?: string;
  showType?: ErrorShowType;
};

export class BizError extends Error {
  info: ResponseStructure;
  request?: Request;

  constructor(info: ResponseStructure, request?: Request) {
    super(info.errorMessage);
    this.name = 'BizError';
    this.info = info;
    this.request = request;
  }
}

export function getBizErrorInfo(error: unknown) {
  return error instanceof BizError ? error.info : undefined;
}

export class RequestError extends Error {
  response?: Response;
  request?: Request;

  constructor(
    messageText: string,
    options?: { response?: Response; request?: Request },
  ) {
    super(messageText);
    this.name = 'RequestError';
    this.response = options?.response;
    this.request = options?.request;
  }
}

export class ResponseParseError extends Error {
  constructor() {
    super('Invalid JSON response');
    this.name = 'ResponseParseError';
  }
}

export function getRequestErrorMessage(error: unknown, intl = getIntl()) {
  if (error instanceof ResponseParseError) {
    return intl.formatMessage({ id: 'app.request.parseError' });
  }

  const info = getBizErrorInfo(error);
  if (info) {
    return info.errorMessage ?? intl.formatMessage({ id: 'app.request.failed' });
  }

  if (error instanceof RequestError && error.response) {
    const { status } = error.response;
    if (status === 403) {
      return intl.formatMessage({ id: 'app.request.forbidden' });
    }
    return intl.formatMessage({ id: 'app.request.responseStatus' }, { status });
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return intl.formatMessage({ id: 'app.request.offline' });
  }
  if (error instanceof Error && error.name === 'TimeoutError') {
    return intl.formatMessage({ id: 'app.request.timeout' });
  }
  if (error instanceof RequestError) {
    return intl.formatMessage({ id: 'app.request.noResponse' });
  }
  return intl.formatMessage({ id: 'app.request.unexpected' });
}

export function isUnauthorizedError(error: unknown) {
  if (error instanceof RequestError) {
    return (
      error.response?.status === 401 &&
      (!error.request || isApiOrigin(error.request.url))
    );
  }

  return (
    error instanceof BizError &&
    (error.info.errorCode === 401 || error.info.errorCode === '401') &&
    (!error.request || isApiOrigin(error.request.url))
  );
}

function isApiOrigin(url: string) {
  return (
    new URL(url, window.location.origin).origin ===
    new URL(
      import.meta.env.VITE_API_BASE_URL?.trim() || window.location.origin,
      window.location.origin,
    ).origin
  );
}

function isResponseStructure(value: unknown): value is ResponseStructure {
  return Boolean(value && typeof value === 'object' && 'success' in value);
}

export function redirectToLogin() {
  resetSession();

  const pathname = toRouterPath(window.location.pathname);
  if (pathname === '/user/login') {
    return;
  }

  const redirect = encodeURIComponent(
    `${pathname}${window.location.search}${window.location.hash}`,
  );
  window.location.replace(toExternalAppPath(`/user/login?redirect=${redirect}`));
}

export const errorConfig = {
  errorConfig: {
    errorThrower: (response: unknown, request?: Request) => {
      if (isResponseStructure(response) && !response.success) {
        throw new BizError(response, request);
      }
    },
    errorHandler: (error: unknown, options?: RequestOptions) => {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (isUnauthorizedError(error)) {
        redirectToLogin();
        return;
      }

      if (options?.skipErrorHandler) {
        return;
      }

      if (error instanceof BizError) {
        const { errorMessage, errorCode, showType } = error.info;
        switch (showType) {
          case ErrorShowType.SILENT:
            break;
          case ErrorShowType.WARN_MESSAGE:
            message.warning(errorMessage);
            break;
          case ErrorShowType.ERROR_MESSAGE:
            message.error(errorMessage);
            break;
          case ErrorShowType.NOTIFICATION:
            notification.open({
              title: String(
                errorCode ??
                  getIntl().formatMessage({
                    id: 'app.request.errorTitle',
                    defaultMessage: 'Request error',
                  }),
              ),
              description: errorMessage,
            });
            break;
          case ErrorShowType.REDIRECT:
            redirectToLogin();
            break;
          default:
            message.error(getRequestErrorMessage(error));
        }
        return;
      }

      message.error(getRequestErrorMessage(error));
    },
  },
  requestInterceptors: [
    (options: RequestOptions, requestUrl?: string): RequestOptions => {
      const token = browserStorage.getItem('token');
      if (!token || !isApiOrigin(requestUrl ?? window.location.origin)) {
        return options;
      }

      if (
        Object.keys(options.headers ?? {}).some(
          (key) => key.toLowerCase() === 'authorization',
        )
      ) {
        return options;
      }

      return {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    },
  ],
  responseInterceptors: [] as Array<
    (response: Response, data: unknown) => Response | Promise<Response>
  >,
};
